import { useEffect, useState } from 'react';
import { type InfiniteData, type QueryKey, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { type CallArgs, type CallInput, infiniteOptionsFor, isApiError, keyForDomain, queryOptionsFor } from '@missions/api-client';
import type { EndpointKey, EndpointResponse, Page } from '@missions/types';
import { useToast } from '@missions/ui';
import { api } from './client';

/** useQuery over a manifest endpoint: `useApi('missions.get', { params: { id } })`. */
export function useApi<K extends EndpointKey>(key: K, ...args: [...CallArgs<K>, { enabled?: boolean; staleTime?: number }?]) {
  const [input, opts] = args as unknown as [CallArgs<K>[0], { enabled?: boolean; staleTime?: number } | undefined];
  return useQuery({ ...queryOptionsFor(api, key, ...([input] as unknown as CallArgs<K>)), enabled: opts?.enabled ?? true, staleTime: opts?.staleTime });
}

/** Invalidate every cached query of one or more endpoint domains (e.g. 'missions', 'notifications'). */
export function useInvalidate() {
  const qc = useQueryClient();
  return (...domains: string[]) =>
    Promise.all(
      domains.map((d) => qc.invalidateQueries({ predicate: (q) => isDomain(q.queryKey, d) })),
    );
}
const isDomain = (key: QueryKey, domain: string): boolean => {
  const k = key[1];
  return typeof k === 'string' && (k === domain || k.startsWith(`${domain}.`));
};
export { keyForDomain };

/** Shows an error toast for API failures (validation errors are handled inline by forms). */
export function useErrorToast() {
  const toast = useToast();
  return (e: unknown, fallback = 'Something went wrong. Please try again.') => {
    if (isApiError(e)) toast(e.message || fallback, { type: 'error' });
    else toast(fallback, { type: 'error' });
  };
}

export function useMediaQuery(q: string): boolean {
  const [m, setM] = useState(() => (typeof window !== 'undefined' ? window.matchMedia(q).matches : false));
  useEffect(() => {
    const mq = window.matchMedia(q);
    const on = () => setM(mq.matches);
    mq.addEventListener('change', on);
    on();
    return () => mq.removeEventListener('change', on);
  }, [q]);
  return m;
}

export const usePhone = () => useMediaQuery('(max-width: 767px)');

/** Persisted boolean in localStorage (private mode safe). */
export function useStoredFlag(key: string, initial = false): [boolean, (v: boolean) => void] {
  const [v, setV] = useState<boolean>(() => {
    try {
      const s = localStorage.getItem(key);
      return s === null ? initial : s === '1';
    } catch {
      return initial;
    }
  });
  const set = (nv: boolean) => {
    setV(nv);
    try {
      localStorage.setItem(key, nv ? '1' : '0');
    } catch {
      /* private mode */
    }
  };
  return [v, set];
}

/* endpoints whose response is a cursor Page (the dist .d.ts widens `paginated: true`, so infer from the response) */
type PagedKey = { [K in EndpointKey]: EndpointResponse<K> extends Page<unknown> ? K : never }[EndpointKey];
type ItemOf<K extends EndpointKey> = EndpointResponse<K> extends Page<infer T> ? T : never;

/** Cursor-paginated list ("Load more", infinite scroll): `useInfinite('missions.list', { query })`. */
export function useInfinite<K extends PagedKey>(key: K, input: Omit<CallInput<K>, 'signal'>, opts?: { enabled?: boolean }) {
  const o = infiniteOptionsFor(api, key as never, input as never) as unknown as {
    queryKey: QueryKey;
    queryFn: (c: { pageParam: string | undefined; signal: AbortSignal }) => Promise<Page<ItemOf<K>>>;
    getNextPageParam: (last: Page<ItemOf<K>>) => string | undefined;
  };
  return useInfiniteQuery<Page<ItemOf<K>>, Error, InfiniteData<Page<ItemOf<K>>, string | undefined>, QueryKey, string | undefined>({
    queryKey: o.queryKey,
    initialPageParam: undefined,
    queryFn: ({ pageParam, signal }) => o.queryFn({ pageParam, signal }),
    getNextPageParam: (last) => o.getNextPageParam(last),
    enabled: opts?.enabled ?? true,
  });
}
